import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import styles from "./AccountDetails.module.css";
import cn from 'classnames';
import { Card, Image } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomButton, { ButtonRadiusSize, ButtonType } from '../../../../../component/common/CustomButton/CustomButton';
import { ASSESTS } from '../../../../../constant/assest';
import FormField, { IFieldTheme, IFieldType } from '../../../../../component/common/FormField/FormField';
import PreviewField from '../../../../../component/common/FormField/PreviewField';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../redux/store';
import { updateAdminProfile, checkUsernameUniqueness } from '../../../../../redux/slices/accountSettings/actions';
import { resetUsernameUniqueness, clearMessage, clearError } from '../../../../../redux/slices/accountSettings/slice';
import { USER_TYPE_LABELS } from '../../../../../component/UI/ReviewPopup/ReviewPopup';
import { toast } from 'react-toastify';
import FormTextarea from '../../../../../component/common/FormField/FormTextArea';
import { getColorForUser } from '../../../../../utils/index';
import { useDebounce } from '../../../../../hooks/useDebounce';

interface IProps {
    className?: string;
};

const validationSchema = Yup.object({
    full_name: Yup.string().trim().required('Full name is required'),
    username: Yup.string().trim().required('Username is required')
    .matches(/^\S+$/, 'Username cannot contain spaces'),
    user_bio: Yup.string().trim().max(400, 'Bio cannot be more than 400 characters'),
});

const AccountDetails: FunctionComponent<IProps> = ({className}) => {
    const [editMode, setEditMode] = useState<boolean>(false);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);
    const {
        adminDetailsUpdating,
        adminDetailsUpdateMessage,
        adminDetailsUpdateError,
        checkingUsernameUniqueness,
        isUsernameUnique,
        usernameUniqueError,
    } = useSelector((state: RootState) => state.accountSettings);

    const formik = useFormik({
        initialValues: {
            full_name: user?.full_name || '',
            username: user?.username || '',
            email: user?.email || '',
            user_bio: user?.user_bio || '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                await dispatch( updateAdminProfile({
                    full_name: values.full_name.trim(),
                    username: values.username.trim(),
                    userbio: values.user_bio.trim(),
                    profile_image: imageFile || undefined,
                })).unwrap();
                setEditMode(false);
                setImageFile(null);
                setImagePreview('');
            } catch (err) {
                console.error('Update profile failed:', err);
            }
        },
    });

    const debouncedUsername = useDebounce( formik.values.username.trim(), 500 );

    useEffect(() => {
        if (!editMode) return;
        const username = debouncedUsername;
        const isModified = username !== user?.username;
        // Stop if empty
        if (!username) return;
        // Stop if Formik validation fails
        if (formik.errors.username) return;
        // Stop if not modified
        if (!isModified) return;
        //  Finally call API
        dispatch(checkUsernameUniqueness(username));
    }, [debouncedUsername, editMode]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(resetUsernameUniqueness());
        };
    }, [dispatch]);

    useEffect(()=> {
        if(adminDetailsUpdateMessage){
            toast.success(adminDetailsUpdateMessage);
            dispatch(clearMessage());
        }
        if(adminDetailsUpdateError){
            toast.error(adminDetailsUpdateError);
            dispatch(clearError());
        }
    }, [dispatch, adminDetailsUpdateMessage, adminDetailsUpdateError])
    

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };
    
    const handleCancel = () => {
        formik.resetForm();
        setImageFile(null);
        setImagePreview('');
        setEditMode(false);
        dispatch(resetUsernameUniqueness());
    }

    const bgColor = useMemo(() => getColorForUser(user?.full_name || user?.username || 'N/A'), [user]);

    if (!user) {
        return <p>No details found!</p>
    }

    const isUsernameModified = formik.values.username.trim() !== user.username;
    const shouldShowUsernameValidation = editMode && isUsernameModified && formik.values.username.trim() !== '' && !formik.errors.username;

    let usernameFieldProps: any = {
        ...formik.getFieldProps('username'),
        label: "Username",
        fieldType: IFieldType.TEXT,
        fieldTheme: IFieldTheme.LIGHT,
        // onBlur: handleUsernameBlur,
        errorText: formik.errors.username,
        showError: !!(formik.touched.username && formik.errors.username),
    };

    if (shouldShowUsernameValidation) {
        usernameFieldProps = {
            ...usernameFieldProps,
            isLoading: checkingUsernameUniqueness,
            isSuccess: !checkingUsernameUniqueness && isUsernameUnique === true,
            successText: "Username is available.",
            errorText: (usernameUniqueError?.message || 'Username is already taken.'),
            showError: (!checkingUsernameUniqueness && isUsernameUnique === false) || !!(formik.touched.username && formik.errors.username),
        };
    }

    const isSaveButtonDisabled = 
        adminDetailsUpdating ||
        (shouldShowUsernameValidation && checkingUsernameUniqueness) || // Disable if checking uniqueness
        (shouldShowUsernameValidation && isUsernameUnique === false) || // Disable if username not unique
        (!formik.dirty && !imageFile); // Original formik disabled logic

    const displayImage = imagePreview || user.profile_image;

    return (
        <Card bsPrefix={cn(styles.accountDetail, className, editMode && styles.editMode)}>
                <Card.Header bsPrefix={cn(styles.header)}>
                    <div className={styles.userDetailWrap}>
                        <div className={styles.userImage}>
                            {editMode ? 
                            <>
                                <div 
                                    className={cn(styles.userImageItem, !displayImage && styles.userAvatarText)}
                                    style={!displayImage ? { backgroundColor: bgColor } : undefined}
                                >
                                    {displayImage ? 
                                        <Image src={displayImage} alt={user.full_name || ''} width={94} height={94} /> :
                                        (user.full_name ? user.full_name.trim().charAt(0).toUpperCase() : '?')
                                    }
                                </div>
                                <label htmlFor="changeProfileImage" className={styles.changeProfileImage}>
                                    <Image src={ASSESTS.CAMERA} alt="" width={18} height={16} />
                                </label>
                                <input
                                    id="changeProfileImage"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: "none" }}
                                />
                            </>
                            :
                            <div 
                                className={cn(styles.userImageItem, !user.profile_image && styles.userAvatarText)}
                                style={!user.profile_image ? { backgroundColor: bgColor } : undefined}
                            >
                                {user.profile_image ?
                                    <Image src={user.profile_image} 
                                        alt={user.full_name || ''}
                                        width={94} height={94} 
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = ''; // To trigger fallback
                                        }}
                                    /> :
                                    (user.full_name ? user.full_name.trim().charAt(0).toUpperCase() : '?')
                                }
                            </div>
                            }
                        </div>
                        <div className={styles.userDetails}>
                            <div className={styles.userName}>{user.full_name}</div>
                            <div className={styles.userLabel}>
                                <span className={styles.userPosition}>{USER_TYPE_LABELS[user.role]}</span>
                            </div>
                            <div className={styles.userEmail}>{user.email}</div>
                        </div>
                    </div>
                    <div className={styles.options}>
                        {editMode ? 
                            <>
                                <CustomButton
                                    className={cn(styles.edituserBtn, styles.saveBtn)}
                                    btnType={ButtonType.GREEN}
                                    onClick={() => formik.handleSubmit()}
                                    btnRadius={ButtonRadiusSize.SMALL}
                                    disabled={isSaveButtonDisabled}
                                >
                                    {adminDetailsUpdating ? 'Saving...' : 'Save'}
                                </CustomButton>
                                <CustomButton
                                    className={cn(styles.edituserBtn, styles.cancelBtn)}
                                    btnType={ButtonType.RED}
                                    onClick={handleCancel}
                                    btnRadius={ButtonRadiusSize.SMALL}
                                >
                                    Cancel
                                </CustomButton>
                            </>
                        :
                             <CustomButton
                                className={styles.edituserBtn}
                                btnType={ButtonType.GREEN}
                                type="button"
                                onClick={() => setEditMode(true)}
                                btnRadius={ButtonRadiusSize.SMALL}
                            >
                                <Image src={ASSESTS.EDIT} alt="Edit" width={16} height={16} /> Edit
                            </CustomButton>
                        }
                    </div>
                </Card.Header>
                <Card.Body bsPrefix={styles.body}>
                    <Card.Header bsPrefix={styles.headerTitle} as={'div'}>Personal Information</Card.Header>
                    <div className={styles.formFields}>
                        <div className={styles.formWrap}>
                            <div className={styles.formCol}>
                                {editMode ? (
                                    <FormField
                                        {...usernameFieldProps}
                                    />
                                ) : (
                                    <PreviewField title="Username" text={user.username || ''} />
                                )}
                            </div>
                            <div className={styles.formCol}>
                                {editMode ? (
                                    <FormField
                                        label="Email"
                                        fieldType={IFieldType.TEXT}
                                        fieldTheme={IFieldTheme.LIGHT}
                                        {...formik.getFieldProps('email')}
                                        disabled
                                    />
                                ) : (
                                    <PreviewField title="Email Address" text={user.email || ''} />
                                )}
                            </div>
                        </div>
                        <div className={styles.formWrap}>
                            <div className={styles.formCol}>
                                {editMode ? (
                                    <FormField
                                        label="Full Name"
                                        fieldType={IFieldType.TEXT}
                                        fieldTheme={IFieldTheme.LIGHT}
                                        {...formik.getFieldProps('full_name')}
                                        errorText={formik.errors.full_name}
                                        showError={!!(formik.touched.full_name && formik.errors.full_name)}
                                    />
                                ) : (
                                    <PreviewField title="Full Name" text={user.full_name || ''} />
                                )}
                            </div>
                        </div>
                        {/* <div className={cn(styles.formWrap , styles.formCol)}> */}
                        <div className={styles.formWrap}>
                            <div className={styles.formCol}>
                                {editMode ? (
                                    <FormTextarea
                                        label="Bio"
                                        fieldTheme={IFieldTheme.LIGHT}
                                        {...formik.getFieldProps('user_bio')}
                                        errorText={formik.errors.user_bio}
                                        showError={!!(formik.touched.user_bio && formik.errors.user_bio)}
                                    />
                                ) : (
                                    <PreviewField title="Bio" text={user.user_bio} />
                                )}
                            </div>
                        </div>
                    </div>
                </Card.Body>
        </Card>
    );
};

export default AccountDetails;